import {
  unassignRenderer,
  assignRenderer,
  priceRenderer,
  costRenderer,
  podRenderer,
  scheduledTaskRenderer,
  statusRenderer,
  taskRenderer,
  chatRenderer,
  callRenderer,
  offersRenderer,
  journeyLaunchedRenderer,
  journeyCreatedFromItineraryRenderer,
  bidOfferRenderer,
  shipperOrderBrokenRenderer,
  journeyBrokenRenderer,
  unassignTaskRenderer,
  xDockRenderer,
  trackingStartRenderer,
  csaJourneyOwnershipRenderer,
  urlRenderer,
  soReturnCreatedRenderer,
  emailToShipperRenderer,
  emailPlannedToShipperRenderer,
  updateShipperOrderRenderer,
  createShipperOrderRenderer,
  scanSkippedRenderer,
  loadScannedRenderer,
  improvedOffersRenderer,
  xDockStopUpdateRenderer,
  xDockIncidentRenderer,
  changeCrossDockDecisionsRenderer,
  journeyUpgradedRenderer,
} from './renderer';
import updateAppointmentRenderer from 'redux/modules/log/renderer/shipperOrderRenderer/updateAppointmentRenderer';
import { SERVICES } from 'tenant/config';

const priceChange = ({
  net_full_price_before,
  net_full_price_after,
  currency_before,
  currency_after,
}) => ({
  net_full_price_before,
  net_full_price_after,
  currency_before,
  currency_after,
});

const costChange = ({
  net_full_cost_before,
  net_full_cost_after,
  currency_before,
  currency_after,
}) => ({
  net_full_cost_before,
  net_full_cost_after,
  currency_before,
  currency_after,
});

const podChange = ({ stop_company_name, stop, cargo_manifest_url }) => ({
  stop_company_name,
  stop,
  cargo_manifest_url,
});

const scanChange = ({ location, total_pallets, scanned_pallets, observations }) => ({
  location,
  total_pallets,
  scanned_pallets,
  observations,
});

const logChanges = {
  UPDATE_PRICE: priceChange,
  UPDATE_PRICE_BY_INCIDENT: priceChange,
  UPDATE_PRICE_BY_STANDSTILL: priceChange,
  UPDATE_COST_INCREMENT: costChange,
  AUTOMATIC_COST_INCREMENT_APPLIED: costChange,
  AUTOMATIC_PENALTY_APPLIED: costChange,
  UPDATE_BONUS: costChange,
  UPDATE_PENALTY: costChange,
  UNASSIGN_DRIVER: ({ driver_contact_name }) => ({
    driver_contact_name,
  }),
  UNASSIGNED_BY_DRIVER: ({ driver_contact_name }) => ({
    driver_contact_name,
  }),
  ASSIGN_DRIVER: ({
    carrier_id_before,
    driver_id_before,
    carrier_id_after,
    driver_id_after,
    carrier_name_before,
    carrier_name_after,
    driver_name_before,
    driver_name_after,
    vehicle_plate_number_before,
    vehicle_plate_number_after,
    trailer_plate_number_before,
    trailer_plate_number_after,
  }) => ({
    carrier_id_before,
    driver_id_before,
    carrier_id_after,
    driver_id_after,
    carrier_name_before,
    carrier_name_after,
    driver_name_before,
    driver_name_after,
    vehicle_plate_number_before,
    vehicle_plate_number_after,
    trailer_plate_number_before,
    trailer_plate_number_after,
  }),
  ACCEPTED_OFFER_BY_DRIVER: ({
    carrier_id,
    driver_id,
    carrier_name_before,
    carrier_name_after,
    driver_name_before,
    driver_name_after,
    vehicle_plate_number_before,
    vehicle_plate_number_after,
    trailer_plate_number_before,
    trailer_plate_number_after,
  }) => ({
    carrier_id,
    driver_id,
    carrier_name_before,
    carrier_name_after,
    driver_name_before,
    driver_name_after,
    vehicle_plate_number_before,
    vehicle_plate_number_after,
    trailer_plate_number_before,
    trailer_plate_number_after,
  }),
  DELETE_CARGO_MANIFEST: podChange,
  UPLOAD_CARGO_MANIFEST: podChange,
  CHANGE_JOURNEY_STATUS: ({ stage, stop_type, stop_company_name }) => ({
    stage,
    stop_type,
    stop_company_name,
  }),
  CARRIER_CALLED_THE_WAREHOUSE: ({ stop, carrier }) => ({
    stop,
    carrier,
  }),
  DRIVER_ASSIGNED_AUTOMATICALLY: ({
    carrier_id,
    driver_id,
    carrier_name_after,
    driver_name_after,
  }) => ({
    carrier_id,
    driver_id,
    carrier_name_after,
    driver_name_after,
  }),
  JOURNEY_LAUNCHED_TO_POOL: ({ drivers, flow_type }) => ({
    drivers,
    flow_type,
  }),
  JOURNEY_CREATED_FROM_ITINERARY: ({ source }) => ({
    source,
  }),
  BID_OFFER_BY_DRIVER: ({ carrier_name_after, driver_name_after, currency, cost }) => ({
    carrier_name_after,
    driver_name_after,
    currency,
    cost,
  }),
  JOURNEY_BROKEN: ({ shipper_order_broken_id }) => ({
    shipper_order_broken_id,
  }),
  SHIPPER_ORDER_BROKEN: ({
    shipper_order_broken_id,
    journey_before_breaking_id,
    journey_after_breaking_id,
  }) => ({
    shipper_order_broken_id,
    journey_before_breaking_id,
    journey_after_breaking_id,
  }),
  XDOCK_AUXILIARY_ORDERS_CREATED: (params) => params,
  TRACKING_START: ({ tracking_url }) => tracking_url,
  CSA_JOURNEY_OWNERSHIP: ({ rule_used, current_owner }) => ({
    rule_used,
    current_owner,
  }),
  SO_RETURN_CREATED: (return_for_id) => return_for_id,
  UPDATE_SHIPPER_ORDER: (params) => params,
  SCAN_SKIPPED: ({ location, skip_reason }) => ({ location, skip_reason }),
  LOAD_SCANNED: scanChange,
  LOAD_SCANNED_MISSING: scanChange,
  ACCEPTED_OFFER_IMPROVEMENT_BY_DRIVER: (changes) => changes,
  REJECTED_OFFER_IMPROVEMENT_BY_DRIVER: (changes) => changes,
};

const logChangesRenderer = {
  UNASSIGN_DRIVER: unassignRenderer,
  UNASSIGNED_BY_DRIVER: unassignRenderer,
  ASSIGN_DRIVER: assignRenderer,
  ACCEPTED_OFFER_BY_DRIVER: assignRenderer,
  UPDATE_PRICE: priceRenderer,
  UPDATE_PRICE_BY_INCIDENT: priceRenderer,
  UPDATE_PRICE_BY_STANDSTILL: priceRenderer,
  UPDATE_COST_INCREMENT: costRenderer,
  UPDATE_BONUS: costRenderer,
  UPDATE_PENALTY: costRenderer,
  AUTOMATIC_COST_INCREMENT_APPLIED: costRenderer,
  AUTOMATIC_PENALTY_APPLIED: costRenderer,
  UPLOAD_CARGO_MANIFEST: podRenderer,
  DELETE_CARGO_MANIFEST: podRenderer,
  CHANGE_JOURNEY_STATUS: statusRenderer,
  TASK_CREATED_MANUALLY: taskRenderer,
  TASK_CREATED_AUTOMATICALLY: taskRenderer,
  TASK_CREATED_DRIVER: xDockIncidentRenderer,
  TASK_CREATED_XDOCK: xDockIncidentRenderer,
  TASK_SOLVED_MANUALLY: taskRenderer,
  TASK_SOLVED_AUTOMATICALLY: taskRenderer,
  TASK_ASSIGNED_TO_NEW_OWNER: taskRenderer,
  TASK_UNASSIGNED_OWNER_UNAVAILABLE: unassignTaskRenderer,
  TASK_UPDATED: taskRenderer,
  TASK_TO_PENDING_REVIEW: taskRenderer,
  TASK_REVIEWED: taskRenderer,
  TASK_SCHEDULED_MANUALLY: scheduledTaskRenderer,
  TASK_SCHEDULED_AUTOMATICALLY: scheduledTaskRenderer,
  CHAT_TASK_SOLVED: taskRenderer,
  CHAT_TASK_CREATED: taskRenderer,
  CARRIER_CALLED_THE_WAREHOUSE: callRenderer,
  EMAIL_OFFER_SENT: offersRenderer,
  DRIVER_ASSIGNED_AUTOMATICALLY: assignRenderer,
  JOURNEY_LAUNCHED_TO_POOL: journeyLaunchedRenderer,
  JOURNEY_CREATED_FROM_ITINERARY: journeyCreatedFromItineraryRenderer,
  BID_OFFER_BY_DRIVER: bidOfferRenderer,
  JOURNEY_BROKEN: journeyBrokenRenderer,
  SHIPPER_ORDER_BROKEN: shipperOrderBrokenRenderer,
  XDOCK_AUXILIARY_ORDERS_CREATED: xDockRenderer,
  TRACKING_START: trackingStartRenderer,
  CSA_JOURNEY_OWNERSHIP: csaJourneyOwnershipRenderer,
  DRIVER_NOT_ASSIGNED_YET: SERVICES.ZENDESK ? urlRenderer('zendesk_link') : () => null,
  STANDSTILL_PICKUP_WARNING_TO_SHIPPER: SERVICES.ZENDESK ? urlRenderer('zendesk_link') : () => null,
  STANDSTILL_DROPOFF_WARNING_TO_SHIPPER: SERVICES.ZENDESK
    ? urlRenderer('zendesk_link')
    : () => null,
  SO_RETURN_CREATED: soReturnCreatedRenderer,
  EMAIL_TO_SHIPPER_SENT: emailToShipperRenderer,
  POSSIBLE_EMAIL_TO_SHIPPER: emailPlannedToShipperRenderer,
  UPDATE_SHIPPER_ORDER: updateShipperOrderRenderer,
  EDIT_SHIPPER_ORDER_BY_SHIPPER: updateShipperOrderRenderer,
  APPOINTMENT_BOOKED: updateAppointmentRenderer,
  CREATE_SHIPPER_ORDER: createShipperOrderRenderer,
  SCAN_SKIPPED: scanSkippedRenderer,
  LOAD_SCANNED: loadScannedRenderer,
  LOAD_SCANNED_MISSING: loadScannedRenderer,
  ACCEPTED_OFFER_IMPROVEMENT_BY_DRIVER: improvedOffersRenderer,
  REJECTED_OFFER_IMPROVEMENT_BY_DRIVER: improvedOffersRenderer,
  JOURNEY_STOP_TIME_UPDATED: xDockStopUpdateRenderer,
  CHAT_SENT_AUTOMATICALLY: chatRenderer,
  CHANGE_CROSS_DOCK_DECISIONS: changeCrossDockDecisionsRenderer,
  JOURNEY_UPGRADED: journeyUpgradedRenderer,
};

const logChangesGenerator = {
  get: (action, changes) => {
    if (!logChanges[action]) {
      console.error(`Not found logChanges generator for ${action} action`);
      return;
    }

    return logChanges[action](changes);
  },
};

export { logChangesGenerator, logChangesRenderer };
export default logChanges;
